import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Trash2, Search, Filter, RefreshCw, Database, 
  Calendar, MapPin, User, Flag, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function ManageRounds() {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rounds, setRounds] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!loading && !user?.is_admin) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchRounds();
    }
  }, [user]);

  const fetchRounds = async () => {
    setLoadingData(true);
    try {
      const res = await fetch(`${API}/admin/all-rounds?limit=200`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRounds(data.rounds || []);
      }
    } catch (err) {
      toast.error('Failed to load rounds');
    } finally {
      setLoadingData(false);
    }
  };

  const handleDelete = async (round) => {
    if (!window.confirm(`Delete ${round.user_name}'s round at ${round.course_name}?`)) return;
    
    setDeleting(round.id);
    try {
      const res = await fetch(
        `${API}/admin/round/${round.source}/${encodeURIComponent(round.id)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      
      if (res.ok) {
        setRounds(prev => prev.filter(r => !(r.id === round.id && r.source === round.source)));
        toast.success('Round deleted');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete round');
    } finally {
      setDeleting(null);
    }
  };

  const getSourceBadge = (source) => {
    const colors = {
      live_rounds: 'bg-blue-100 text-blue-700',
      social_posts: 'bg-green-100 text-green-700',
      leaderboard_entries: 'bg-purple-100 text-purple-700',
    };
    const labels = {
      live_rounds: 'Live Scorecard',
      social_posts: 'Bundle Feed',
      leaderboard_entries: 'Leaderboard',
    };
    return (
      <Badge className={`${colors[source] || 'bg-gray-100 text-gray-700'} text-xs`}>
        {labels[source] || source}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredRounds = rounds.filter(r => {
    const matchesSearch = !searchTerm || 
      r.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.course_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSource = sourceFilter === 'all' || r.source === sourceFilter;
    return matchesSearch && matchesSource;
  });

  // Group by source for stats
  const sourceStats = {
    live_rounds: rounds.filter(r => r.source === 'live_rounds').length,
    social_posts: rounds.filter(r => r.source === 'social_posts').length,
    leaderboard_entries: rounds.filter(r => r.source === 'leaderboard_entries').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bundle-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-white rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-bundle-green" />
          </button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-bundle-green">Manage All Rounds</h1>
            <p className="text-sm text-gray-600">View and delete rounds from all data sources</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRounds}
            className="ml-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              if (!window.confirm('DELETE ALL ROUNDS? This will clear all leaderboards. This cannot be undone!')) return;
              if (!window.confirm('Are you ABSOLUTELY sure? Type reason in next prompt.')) return;
              try {
                const res = await fetch(`${API}/admin/clear-all-rounds`, {
                  method: 'DELETE',
                  credentials: 'include'
                });
                if (res.ok) {
                  const data = await res.json();
                  toast.success(`Deleted ${data.deleted_count} rounds`);
                  fetchRounds();
                }
              } catch (err) {
                toast.error('Failed to clear rounds');
              }
            }}
            className="ml-2"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <Database className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-blue-700">{sourceStats.live_rounds}</p>
              <p className="text-xs text-blue-600">Live Scorecard</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <Database className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-green-700">{sourceStats.social_posts}</p>
              <p className="text-xs text-green-600">Bundle Feed</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 text-center">
              <Database className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-purple-700">{sourceStats.leaderboard_entries}</p>
              <p className="text-xs text-purple-600">Leaderboard Entries</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by player or course..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bundle-green focus:border-bundle-green"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={sourceFilter}
                  onChange={e => setSourceFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bundle-green"
                >
                  <option value="all">All Sources</option>
                  <option value="live_rounds">Live Scorecard</option>
                  <option value="social_posts">Bundle Feed</option>
                  <option value="leaderboard_entries">Leaderboard</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rounds Table */}
        <Card>
          <CardHeader className="border-b border-gray-200">
            <CardTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-bundle-green" />
              All Rounds ({filteredRounds.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingData ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
              </div>
            ) : filteredRounds.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No rounds found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredRounds.map((round, idx) => (
                  <div 
                    key={`${round.source}-${round.id}-${idx}`}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    {/* Player */}
                    <div className="w-40">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900 truncate">{round.user_name || 'Unknown'}</span>
                      </div>
                    </div>
                    
                    {/* Course */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700 truncate">{round.course_name}</span>
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="w-20 text-center">
                      <span className="font-heading text-xl font-bold text-bundle-green">{round.score}</span>
                      {round.score_vs_par != null && (
                        <span className={`block text-xs ${round.score_vs_par < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {round.score_vs_par > 0 ? '+' : ''}{round.score_vs_par}
                        </span>
                      )}
                    </div>
                    
                    {/* Birdies */}
                    <div className="w-16 text-center">
                      {(round.birdies > 0 || round.stats?.birdies > 0) && (
                        <span className="text-sm text-green-600">
                          {round.birdies || round.stats?.birdies} 🐦
                        </span>
                      )}
                    </div>
                    
                    {/* Date */}
                    <div className="w-28 text-right">
                      <div className="flex items-center justify-end gap-1 text-sm text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(round.date)}
                      </div>
                    </div>
                    
                    {/* Source */}
                    <div className="w-28">
                      {getSourceBadge(round.source)}
                    </div>
                    
                    {/* Delete */}
                    <div className="w-12">
                      <button
                        onClick={() => handleDelete(round)}
                        disabled={deleting === round.id}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                      >
                        {deleting === round.id ? (
                          <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
